import type {
    SelectInstance,
    SelectComponentsConfig,
    GroupBase,
    Props,
    OptionProps,
    MenuProps,
    ControlProps,
    DropdownIndicatorProps,
    ClearIndicatorProps,
    MultiValueRemoveProps
} from 'react-select'
import type { ReactNode, Ref } from 'react'
import { forwardRef, useMemo } from 'react'
import { isNil } from 'lodash'
import ReactSelect, { components } from 'react-select'
import { ChevronDown, Info, Loader, X } from 'lucide-react'
import Icon from '@/shared/v2/shared/components/Icon'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import Tooltip from './Tooltip'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

const optionStyles = cva(['flex items-center'], {
    variants: {
        optionHeight: {
            small: 'h-6',
            medium: 'h-9',
            large: 'h-12'
        }
    },
    defaultVariants: {
        optionHeight: 'medium'
    }
})

const optionInnerStyles = cva(['flex items-center cursor-pointer text-textPrimary px-2 w-full text-sm rounded-1'], {
    variants: {
        optionHeight: {
            small: 'h-5',
            medium: 'h-8',
            large: 'h-11'
        },
        isDisabled: {
            true: 'opacity-50 cursor-not-allowed',
            false: ''
        },
        isFocused: {
            true: 'bg-backgroundPrimaryHover',
            false: ''
        },
        isSelected: {
            true: 'bg-backgroundPrimarySelect text-textSelect hover:bg-backgroundPrimarySelect',
            false: ''
        }
    },
    compoundVariants: [
        {
            isSelected: true,
            isFocused: true,
            className: 'bg-backgroundPrimarySelect text-textSelect hover:bg-backgroundPrimarySelect'
        }
    ],
    defaultVariants: {
        optionHeight: 'medium',
        isDisabled: false,
        isFocused: false,
        isSelected: false
    }
})

export interface SelectProps<
    Option,
    IsMulti extends boolean = boolean,
    Group extends GroupBase<Option> = GroupBase<Option>
>
    extends Props<Option, IsMulti, Group>, VariantProps<typeof optionStyles> {
    label?: ReactNode
    message?: string
    required?: boolean
    disabled?: boolean
    'data-testid'?: string
    isInModal?: boolean
    state?: 'complete' | 'error' | 'default' | null
    action?: ReactNode
    tooltip?: ReactNode
}

function Option<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: OptionProps<Option, IsMulti, Group> & VariantProps<typeof optionStyles>
) {
    const { innerProps, children, isFocused, isSelected, isDisabled, optionHeight = 'medium' } = props

    return (
        <div className={optionStyles({ optionHeight })}>
            <div
                {...innerProps}
                className={optionInnerStyles({
                    optionHeight,
                    isDisabled,
                    isFocused,
                    isSelected
                })}
            >
                {children}
            </div>
        </div>
    )
}

function LoadingIndicator({ 'data-testid': dataTestId }: { 'data-testid'?: string }) {
    return (
        <div className="flex items-center justify-center p-2" data-testid={dataTestId}>
            <Icon icon={Loader} className="animate-spin" />
        </div>
    )
}

function Menu<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: MenuProps<Option, IsMulti, Group> & { action?: ReactNode }
) {
    return (
        <components.Menu {...props} className="top-[112%]! rounded-sm shadow-1">
            <div className="bg-backgroundPrimary border-0.5 border-borderPrimary rounded-sm">
                {props.action && <div className="px-2 py-2 border-b-0.5 border-borderPrimary">{props.action}</div>}
                {props.children}
                {props.isLoading && <LoadingIndicator data-testid="__select-loading-bottom__" />}
            </div>
        </components.Menu>
    )
}

function Control<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: ControlProps<Option, IsMulti, Group>
) {
    return (
        <components.Control {...props}>
            <div className="flex items-center justify-between w-full h-full pl-3 pr-2">{props.children}</div>
        </components.Control>
    )
}

function DropdownIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: DropdownIndicatorProps<Option, IsMulti, Group>
) {
    return (
        <components.DropdownIndicator className="p-0" {...props}>
            <Icon icon={ChevronDown} />
        </components.DropdownIndicator>
    )
}

function ClearIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: ClearIndicatorProps<Option, IsMulti, Group>
) {
    return (
        <components.ClearIndicator {...props}>
            <Icon icon={X} data-testid="__select-clear-indicator__" />
        </components.ClearIndicator>
    )
}

function MultiValueRemove<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: MultiValueRemoveProps<Option, IsMulti, Group>
) {
    return (
        <div className="'text-textSecondary hover:bg-inherit hover:text-inherit rounded-r-sm p-0 h-5 flex items-center'">
            <components.MultiValueRemove {...props}>
                <Icon size="1" icon={X} />
            </components.MultiValueRemove>
        </div>
    )
}

function Select<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: SelectProps<Option, IsMulti, Group>,
    ref: Ref<SelectInstance<Option, IsMulti, Group>>
) {
    const {
        label,
        name,
        required,
        state,
        isInModal,
        message,
        menuPlacement,
        disabled,
        action,
        tooltip,
        'data-testid': dataTestId,
        optionHeight = 'medium',
        ...restProps
    } = props

    const customComponents = useMemo<Partial<SelectComponentsConfig<Option, IsMulti, Group>> | undefined>(
        () => ({
            Option: (optionProps) => <Option {...optionProps} optionHeight={optionHeight} />,
            Menu: (menuProps) => <Menu {...menuProps} action={action} />,
            Control,
            DropdownIndicator,
            ClearIndicator,
            MultiValueRemove,
            LoadingIndicator: ({ hasValue }: { hasValue: boolean }) => {
                return hasValue ? null : <LoadingIndicator data-testid="__select-loading-input__" />
            }
        }),
        [action, optionHeight]
    )

    return (
        <div className="w-full" data-testid={dataTestId ?? '__select__'}>
            {label && (
                <label htmlFor={props.name} className="flex text-sm font-medium mb-2" data-testid="__select-label__">
                    {label}
                    {required && !disabled && (
                        <span className="text-textNegative ml-1" data-testid="__select-required-indicator__">
                            *
                        </span>
                    )}
                    {tooltip && (
                        <Tooltip
                            content={tooltip}
                            trigger={
                                <span>
                                    <Icon icon={Info} size="1" className="ml-1" />
                                </span>
                            }
                        />
                    )}
                </label>
            )}
            <ReactSelect
                {...restProps}
                inputId={name}
                ref={ref}
                menuPortalTarget={document.body}
                styles={{
                    valueContainer: (base) => ({
                        ...base,
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        WebkitOverflowScrolling: 'touch'
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        pointerEvents: 'auto'
                    }),
                    placeholder: (base, state) => ({
                        ...base,
                        display:
                            state.hasValue || state.selectProps.inputValue || (state.isFocused && !state.isMulti)
                                ? 'none'
                                : 'block'
                    })
                }}
                maxMenuHeight={isInModal ? 200 : undefined}
                menuPlacement={menuPlacement || 'auto'}
                classNamePrefix="react-select"
                unstyled
                classNames={{
                    control: (props) => `
  w-full bg-backgroundPrimary border-0.5 rounded-2 min-h-8! h-8 flex
  ${props.isDisabled ? '!bg-backgroundDisabled !text-textDisabled' : ''}
  ${state === 'error' ? 'border-borderNegativeSubdued' : ''}
  ${
      state === 'error' && props.isFocused
          ? 'ring-3 ring-opacity-100 border-borderNegativeSubdued ring-borderEmphasisNegative'
          : ''
  }
  ${state !== 'error' && props.isFocused ? 'ring-3 ring-opacity-100 border-borderTertiary ring-borderEmphasis' : ''}
`,
                    indicatorsContainer: () => 'gap-1',
                    valueContainer: (props) =>
                        `${
                            props.isMulti ? 'flex-nowrap overflow-x-auto overflow-y-hidden no-scrollbar' : 'flex'
                        } h-full p-0 gap-2 items-center`,
                    input: () => 'h-full text-inherit m-0 p-0 text-sm',
                    placeholder: () => 'text-sm text-textPlaceholder leading-none',
                    singleValue: () => 'm-0 ml-0.5 text-textPrimary text-sm h-full flex items-center leading-none',
                    indicatorSeparator: () => 'hidden',
                    menuList: () => 'px-1 py-0.5',
                    multiValue: () =>
                        'whitespace-nowrap shrink-0 bg-backgroundNeutralLight rounded-2 px-1.5 gap-1 h-5 flex items-center',
                    multiValueLabel: () => 'text-textPrimary text-xxs font-normal p-0 pr-1 leading-5',
                    noOptionsMessage: () => 'text-textPrimary text-sm p-1 text-textSecondary',
                    loadingMessage: () => 'hidden'
                }}
                components={customComponents}
                isDisabled={disabled}
            />
            {!isNil(message) && (
                <Paragraph size={3} color={state === 'error' ? 'negative' : 'darkGrey'} className="pt-2">
                    {message}
                </Paragraph>
            )}
        </div>
    )
}

// eslint-disable-next-line typescript/no-unsafe-type-assertion -- the value has this shape at runtime, as the code has always read it
export default forwardRef(Select) as <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(
    props: SelectProps<Option, IsMulti, Group> & React.RefAttributes<SelectInstance<Option, IsMulti, Group>>
) => React.ReactElement
